import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { push } from 'connected-react-router';

import StandardLayout from 'components/Layouts/Standard';
import { selectCurrentUserInfo } from 'store/currentUser';
import {
  deactivateChannelRequest,
  getSettingsClean,
  getSettingsRequest,
  selectChannelDiia,
  selectChannelEmail,
  selectSettingsError,
  selectSettingsIsLoading,
} from 'store/settings';
import { ROUTES } from 'constants/routes';
import { ChannelType } from 'types/settings';
import { EmailIcon, LogoDiiaIcon, PointUpIcon } from '#web-components/components/Icons';
import FlashMessage, { ViewType } from '#web-components/components/FlashMessage';
import Typography from '#web-components/components/Typography';
import InlineButton from '#web-components/components/InlineButton';
import EntityInfo from '#web-components/components/EntityInfo';

import styles from './ProfilePage.styles';
import ChannelItem from './components/ChannelItem';
import ModalDiactivateChannel from './components/ModalDiactivateChannel';

const useStyles = makeStyles(styles, { name: 'ProfilePage' });

export default function ProfilePage() {
  const { t } = useTranslation('pages');
  const classes = useStyles();
  const currentUser = useSelector(selectCurrentUserInfo);
  const settingsEmail = useSelector(selectChannelEmail);
  const settingsDiia = useSelector(selectChannelDiia);
  const isLoading = useSelector(selectSettingsIsLoading);
  const getSettingsError = useSelector(selectSettingsError);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [profileChannel, setProfileChannel] = useState<{
    channel: ChannelType,
    value?: string,
  }>({ channel: ChannelType.EMAIL, value: '' });
  const isUaRegion = ENVIRONMENT_VARIABLES.region === 'ua';

  const onEditEmail = useCallback(() => {
    dispatch(push(ROUTES.PROFILE_EDIT, { isEditEmail: true }));
  }, [dispatch]);

  const onCreateEmail = useCallback(() => {
    dispatch(push(ROUTES.PROFILE_EDIT));
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    setOpen(false);
    const { channel } = profileChannel;
    dispatch(deactivateChannelRequest({ channel }));
  }, [dispatch, profileChannel]);

  const handleDeactivate = useCallback((channel: ChannelType, value?: string) => () => {
    setOpen(true);
    setProfileChannel({ channel, value });
  }, []);

  const handleActivate = useCallback((channel: ChannelType, address?: string) => () => {
    const route = ROUTES.PROFILE_CONFIRM_CHANNEL.replace(':channel', channel);
    const query = channel === ChannelType.EMAIL ? `?address=${address}` : '';

    dispatch(push(`${route}${query}`));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getSettingsRequest());
    return () => {
      dispatch(getSettingsClean());
    };
  }, [dispatch]);

  return (
    <StandardLayout
      title={t('profile.title')}
      isLoading={isLoading}
    >

      <Grid container>
        <Grid item sm={8}>
          <EntityInfo
            title={currentUser?.fullName || currentUser?.name || ''}
            data={[
              ...(isUaRegion && currentUser?.drfo
                ? [{
                  label: t('domains:user.rnokpp'),
                  value: currentUser?.drfo,
                  dataXpath: { text: 'user.rnokpp.value' },
                }]
                : []),
              ...(isUaRegion && currentUser?.edrpou
                ? [{
                  label: t('domains:user.edrpou'),
                  value: currentUser?.edrpou,
                  dataXpath: { text: 'user.edrpou.value' },
                }]
                : []),
            ]}
          />
          <div className={classes.settingsTitle}>
            <Typography variant="h3">
              {t('profile.settings.title')}
            </Typography>
          </div>

          <FlashMessage
            status="info"
            customIcon={PointUpIcon}
            message={t('profile.settings.notification')}
            size="large"
            viewType={ViewType.plain}
          />
          {
            getSettingsError && (
              <FlashMessage
                status="error"
                title={t('errors:notification.default.title')}
                message={t('profile.settings.errorNotification')}
                viewType={ViewType.plain}
              />
            )
          }

          { isUaRegion && (
            <div className={classes.borderBottom}>
              <ChannelItem
                label={t('profile.settings.pushMessage')}
                channel={getSettingsError ? undefined : t('profile.settings.applicationDiia')}
                placeholder={(
                  <Typography
                    variant="h7"
                    component="span"
                    className={classes.emailMessage}
                  >
                    {t('profile.settings.errorReading')}
                  </Typography>
                )}
                icon={LogoDiiaIcon}
                text={{
                  hint: (
                    <Box
                      component="span"
                    >
                      {t('profile.settings.hintDiia')}
                      <Link
                        className={classes.link}
                        to={{ pathname: 'https://diia.gov.ua/faq/8' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('profile.settings.linkDiia')}
                      </Link>
                    </Box>
                  ),
                  hintActive: t('profile.settings.hintDiiaActive'),
                }}
                isActive={!!settingsDiia?.activated}
                onActivate={handleActivate(ChannelType.DIIA)}
                onDeactivate={handleDeactivate(ChannelType.DIIA, t('profile.settings.applicationDiia'))}
              />
            </div>
          ) }

          <ChannelItem
            label={t('profile.settings.email')}
            channel={settingsEmail?.address}
            placeholder={getSettingsError
              ? (
                <Typography
                  variant="h7"
                  component="span"
                  className={classes.emailMessage}
                >
                  {t('profile.settings.errorReading')}
                </Typography>
              )
              : (
                <InlineButton
                  data-xpath="profile.settings.enterEmailAddress"
                  size="medium"
                  leftIcon={<EmailIcon />}
                  onLinkClick={onCreateEmail}
                >
                  {t('profile.settings.enterEmailAddress')}
                </InlineButton>
              )}
            icon={EmailIcon}
            text={{
              hint: t('profile.settings.hintEmail'),
              hintActive: t('profile.settings.hintEmailActive'),
            }}
            isActive={!!settingsEmail?.activated}
            onEdit={onEditEmail}
            onActivate={handleActivate(ChannelType.EMAIL, settingsEmail?.address)}
            onDeactivate={handleDeactivate(ChannelType.EMAIL, settingsEmail?.address)}
          />

        </Grid>
      </Grid>

      <ModalDiactivateChannel
        channel={profileChannel.value}
        isOpen={open}
        onOpenChange={setOpen}
        onSubmit={handleSubmit}
      />

    </StandardLayout>
  );
}
